import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {vec3} from "#material/types/vector"
import {color} from "@cm/graph-v3"
import {z} from "zod/v4-mini"
import {materialClosureOutput} from "#material/nodes/base/material/validation/io"

export interface BsdfDiffuseSockets extends MaterialNodeSockets {
    inputs: {
        color: "color" // found in inputs: color; found in settings: color; inferred from data: float3
        normal: "normal" // found in inputs: normal; found in settings: vector; inferred from data: float3
        roughness: "float" // found in inputs: -; found in settings: float; inferred from data: float
    }
    settings: {
        color: "color" // found in inputs: color; found in settings: color; inferred from data: float3
        normal: "normal" // found in inputs: normal; found in settings: vector; inferred from data: float3
        roughness: "float" // found in inputs: -; found in settings: float; inferred from data: float
    }
    outputs: {
        bsdf: "closure"
    }
}

export const bsdfDiffuseDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeBsdfDiffuse,
    },
    nodeClass: "BsdfDiffuse",
    ui: {
        label: "Diffuse BSDF",
        color: "#2d8a5d",
        items: [
            {name: "bsdf", label: "BSDF", valueType: "closure", output: true},
            {name: "color", label: "Color", valueType: "color", input: true, setting: true, defaultValue: {r: 0.8, g: 0.8, b: 0.8}},
            {name: "roughness", label: "Roughness", valueType: "float", input: true, setting: true, defaultValue: 0},
            {name: "normal", label: "Normal", valueType: "normal", input: true, setting: true, defaultValue: {x: 0, y: 0, z: 0}},
        ],
        support: {
            cycles: true,
            three: true,
        },
    },
    validation: {
        returns: z.strictObject({bsdf: materialClosureOutput}),
        parameters: z.strictObject({
            inputs: z.strictObject({
                color: optionalMaterialInput,
                normal: optionalMaterialInput,
                roughness: optionalMaterialInput,
            }),
            settings: z.strictObject({
                color: z.optional(color),
                roughness: z.optional(z.number()),
                normal: z.optional(vec3),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<BsdfDiffuseSockets>
